import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  InMemoryCache,
  useApolloClient,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { appHistory } from "appHistory";
import {
  GraphQLErrorHandlingType,
  REACT_APP_GRAPHQL_ERROR_HANDLING_TYPE,
} from "environmentVariables";
import { scalarLink } from "scalarLink";
import { hasValue } from "Tools";
import { PublicPaths } from "Views/Paths";

export const graphqlUserQueryOperationName = "UserQuery";
export const graphqlUnauthenticatedErrorCodes = [
  "AUTH_NOT_AUTHENTICATED",
  "AUTH_NOT_AUTHORIZED",
];

const graphQLUri = process.env.STORYBOOK_GRAPHQL_URL ?? "/graphql";

// https://www.apollographql.com/docs/react/networking/authentication/
export const apolloClient = new ApolloClient({
  cache: new InMemoryCache(),
  link: ApolloLink.from([
    scalarLink,
    onError(error => {
      if (!error.graphQLErrors) {
        // don't redirect on network errors
        return;
      }

      if (
        error.graphQLErrors.some(
          e =>
            hasValue(e.extensions) &&
            graphqlUnauthenticatedErrorCodes.includes(
              e.extensions.code as string,
            ),
        )
      ) {
        if (
          error.operation.operationName !== graphqlUserQueryOperationName && // UserQuery auth errors are handled by AuthenticatedRoute
          !window.location.href.toLowerCase().includes("login")
        ) {
          appHistory.push(
            `${PublicPaths.LogIn}?ReturnUrl=${appHistory.location.pathname}`,
          );
        }
      } else {
        console.warn(error.graphQLErrors);
        switch (REACT_APP_GRAPHQL_ERROR_HANDLING_TYPE) {
          case GraphQLErrorHandlingType.Throw:
            throw new Error("server error"); // shows react-scripts error page
          case GraphQLErrorHandlingType.Ignore:
            break;
          case GraphQLErrorHandlingType.Redirect: // default
            if (
              !window.location.href
                .toLowerCase()
                .includes(PublicPaths.Error.toLowerCase())
            ) {
              appHistory.push(PublicPaths.Error);
              throw new Error("server error"); // exit rest of caller
            }
            break;
        }
      }
    }),
    createHttpLink({
      uri: graphQLUri,
      credentials: "same-origin",
    }),
  ]),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
    },
  },
});

export type TypedApolloClient = ReturnType<typeof useApolloClient>;
